import React, { useState, useEffect, useCallback } from 'react';
import { ReportGenerationService } from '../../services/ReportGenerationService';
import { TemplateType } from '../../types/template.types';
import { useAnalysisReportStore } from '../../stores/analysisReportStore';
import MaximizedTextEditor from './MaximizedTextEditor';

interface StreamingReportEditorProps {
  open: boolean;
  onClose: () => void;
  title: string;
  reportId: string;
  projectId: string;
  estimateId: string;
  estimateName?: string;
  placeholder?: string;
  doneButtonText?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  maxHeight?: string;
  enableTemplates?: boolean;
  suggestedTemplateType?: TemplateType;
  onSave?: (value: string) => void;
}

export const StreamingReportEditor: React.FC<StreamingReportEditorProps> = ({
  open,
  onClose,
  title,
  reportId,
  projectId,
  estimateId,
  estimateName = '',
  placeholder = 'Report content will appear here...',
  doneButtonText = 'Done',
  maxWidth = 'lg',
  maxHeight = '90vh',
  enableTemplates = true,
  suggestedTemplateType = 'general',
  onSave,
}) => {
  const [content, setContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [streamingMessage, setStreamingMessage] = useState<string>('Generating report...');
  const [isLoadingCached, setIsLoadingCached] = useState<boolean>(false);

  const analysisReportStore = useAnalysisReportStore();

  const startStreaming = useCallback(async () => {
    if (!projectId || !estimateId) {
      console.error('Missing required project or estimate ID');
      return;
    }

    // For analysis reports, check cache and cooldown before streaming
    if (reportId === 'item-analysis') {
      const cachedReport = analysisReportStore.getReport(estimateId);
      const isOnCooldown = analysisReportStore.isOnCooldown(estimateId);

      if (cachedReport && isOnCooldown) {
        // Load cached report instead of streaming
        setIsLoadingCached(true);
        setStreamingMessage('Loading cached report...');

        setTimeout(() => {
          setContent(cachedReport.report);
          setIsStreaming(false);
          setIsLoadingCached(false);
        }, 200); // Slightly longer delay to ensure Quill is fully ready
        return;
      }
    }

    setIsStreaming(true);
    setContent(''); // Clear any existing content

    const streamingOptions = {
      projectId,
      estimateId,
      estimateName,
      onChunk: (chunk: string) => {
        setContent(chunk);
      },
      onComplete: (fullContent: string) => {
        setIsStreaming(false);
        setContent(fullContent);
      },
      onError: (error: Error) => {
        setIsStreaming(false);
        console.error('Streaming error:', error);
        setContent(`Error generating report: ${error.message}\n\nPlease try again.`);
      },
      // Always persist reports when component unmounts
      shouldPersistOnUnmount: true,
    };

    try {
      // Determine which report type to generate based on reportId
      if (reportId === 'estimate-report') {
        setStreamingMessage('Generating estimate report...');
        await ReportGenerationService.generateEstimateReport(streamingOptions);
      } else if (reportId === 'item-analysis') {
        setStreamingMessage('Analyzing extended cost items...');
        await ReportGenerationService.generateItemAnalysisReport(streamingOptions);
      } else {
        throw new Error(`Unknown report type: ${reportId}`);
      }
    } catch (error) {
      console.error('Error starting stream:', error);
      setIsStreaming(false);
      setContent(
        `Error generating report: ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease try again.`
      );
    }
  }, [projectId, estimateId, estimateName, reportId, analysisReportStore]);

  // Check for cached content when dialog opens or estimateId changes
  useEffect(() => {
    if (open && reportId === 'item-analysis' && estimateId) {
      const cachedReport = analysisReportStore.getReport(estimateId);
      const isOnCooldown = analysisReportStore.isOnCooldown(estimateId);

      if (cachedReport && isOnCooldown) {
        // Show loading state for cached content
        setIsLoadingCached(true);
        setStreamingMessage('Loading cached report...');

        // Load cached report with a delay to ensure editor is ready
        setTimeout(() => {
          setContent(cachedReport.report);
          setIsStreaming(false);
          setIsLoadingCached(false);
          setIsInitialLoad(false);
        }, 200); // Slightly longer delay to ensure Quill is fully ready
        return;
      }
    }
  }, [open, reportId, estimateId, analysisReportStore]);

  // Start streaming when the editor opens (only if no cached content was loaded)
  useEffect(() => {
    if (open && isInitialLoad) {
      // For non-analysis reports or when no cached content, start streaming
      startStreaming();
      setIsInitialLoad(false);
    }
  }, [open, isInitialLoad, startStreaming, reportId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setContent('');
      setIsStreaming(false);
      setIsLoadingCached(false);
      setIsInitialLoad(true);
      setStreamingMessage('Generating report...');
    }
  }, [open]);

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
  }, []);

  const handleSave = useCallback(
    (value: string) => {
      if (onSave) {
        onSave(value);
      }
      onClose();
    },
    [onSave, onClose]
  );

  return (
    <MaximizedTextEditor
      open={open}
      onClose={onClose}
      value={content}
      onChange={handleContentChange}
      title={title}
      placeholder={placeholder}
      doneButtonText={doneButtonText}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      enableTemplates={enableTemplates}
      suggestedTemplateType={suggestedTemplateType}
      onSave={handleSave}
      isStreaming={isStreaming || isLoadingCached}
      streamingMessage={streamingMessage}
    />
  );
};
